import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { replace } from 'connected-react-router'
import { ApiMiddlewarePayload } from '@ocean/api'
import { AppThunk } from 'store'
import { createApiAction, MiddlewareApiAction } from 'api'
import { userLoggedOut } from 'core/session/slices/session'
import {
  getDistanceCourseDetails,
  uploadDistanceCourseDocument,
  createDistanceCourseResult,
  submitDistanceCourseResult,
  removeDistanceCourseDocument,
} from 'modules/activityDetails/DistanceCourseDetails/api/distanceCourseDetailsService'
import routes from 'modules/activityDetails/DistanceCourseDetails/routing/routes'
import {
  DistanceCourseDetailsState,
  ApiDistanceCourseDetailsResponse,
  ApiDistanceCourseDocumentUploadResponse,
  ApiCreateDistanceCourseResultResponse,
  ApiSubmitDistanceCourseResultResponse,
  ApiDistanceCourseDocumentRemoveResponse,
  ReloadDistanceCourseDetailsParams,
} from './types'

const initialState: DistanceCourseDetailsState = {
  distanceCourseDetailsData: {},
  distanceCourseDocumentUploadRequest: {},
  createDistanceCourseResultRequest: {},
  submitDistanceCourseResultRequest: {},
  showGuidelinesForResults: {},
  distanceCourseDocumentRemoveRequest: {},
}

const distanceCourseDetailsSlice = createSlice({
  name: 'distanceCourseDetails',
  initialState,
  reducers: {
    distanceCourseDetailsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiDistanceCourseDetailsResponse>>
    ) {
      state.distanceCourseDetailsData = action.payload
    },
    clearDistanceCourseDetails(state) {
      state.distanceCourseDetailsData = initialState.distanceCourseDetailsData
    },
    distanceCourseDocumentUploaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiDistanceCourseDocumentUploadResponse>>
    ) {
      state.distanceCourseDocumentUploadRequest = action.payload
    },
    createDistanceCourseResultRequested(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiCreateDistanceCourseResultResponse>>
    ) {
      state.createDistanceCourseResultRequest = action.payload
    },
    submitDistanceCourseResultRequested(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiSubmitDistanceCourseResultResponse>>
    ) {
      state.submitDistanceCourseResultRequest = action.payload
    },
    distanceCourseDocumentRemoved(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiDistanceCourseDocumentRemoveResponse>>
    ) {
      state.distanceCourseDocumentRemoveRequest = action.payload
    },
    addGuidelinesToShow(state, action: PayloadAction<string>) {
      // Save the resultId to display guidelines
      state.showGuidelinesForResults[action.payload] = true
    },
    deleteGuidelinesFromShow(state, action: PayloadAction<string>) {
      delete state.showGuidelinesForResults[action.payload]
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  distanceCourseDetailsLoaded,
  clearDistanceCourseDetails,
  distanceCourseDocumentUploaded,
  createDistanceCourseResultRequested,
  submitDistanceCourseResultRequested,
  deleteGuidelinesFromShow,
  addGuidelinesToShow,
  distanceCourseDocumentRemoved,
} = distanceCourseDetailsSlice.actions

export { clearDistanceCourseDetails, deleteGuidelinesFromShow }
export default distanceCourseDetailsSlice.reducer

export const loadDistanceCourseDetailsData = (distanceCourseId: Guid, resultId?: Guid) => {
  return createApiAction({
    apiCalls: {
      distanceCourseDetails: () => getDistanceCourseDetails(distanceCourseId, resultId),
    },
    payloadAction: distanceCourseDetailsLoaded,
    getResultToKeep: ({ distanceCourseDetails }) =>
      distanceCourseDetails.distanceCourseDetailsData.result,
  })
}

export const reloadDistanceCourseDetails = (distanceCourseId: Guid, resultId?: Guid) => {
  return createApiAction({
    apiCalls: {
      distanceCourseDetails: () => getDistanceCourseDetails(distanceCourseId, resultId),
    },
    payloadAction: distanceCourseDetailsLoaded,
    shouldShowGlobalLoading: true,
    getResultToKeep: ({ distanceCourseDetails }) =>
      distanceCourseDetails.distanceCourseDetailsData.result,
  })
}

const handleReloadDistanceCourseDetails =
  (response: ApiMiddlewarePayload, params: ReloadDistanceCourseDetailsParams): AppThunk =>
  async (dispatch, getState) => {
    const { payloadAction, resultId } = params
    dispatch(payloadAction(response))

    if (!response.error) {
      const { courseId } =
        getState().distanceCourseDetails.distanceCourseDetailsData.result?.distanceCourseDetails ||
        {}
      courseId && dispatch(reloadDistanceCourseDetails(courseId, resultId) as MiddlewareApiAction)
      dispatch(deleteGuidelinesFromShow(resultId))
    }
  }

export const uploadDistanceCourseDocumentRequest = (
  files: File[],
  requirementId: Guid,
  inProgressResultId?: Guid
) => {
  return createApiAction({
    apiCalls: {
      distanceCourseDocumentUpload: () =>
        uploadDistanceCourseDocument(files, requirementId, inProgressResultId),
    },
    payloadAction: distanceCourseDocumentUploaded,
    nextAction: {
      action: handleReloadDistanceCourseDetails,
      params: {
        payloadAction: distanceCourseDocumentUploaded,
        resultId: inProgressResultId,
      },
    },
    enableErrorNotification: true,
  })
}

export const removeDistanceCourseDocumentRequest = (
  requirementId: Guid,
  inProgressResultId: Guid
) => {
  return createApiAction({
    apiCalls: {
      distanceCourseDocumentRemove: () =>
        removeDistanceCourseDocument(requirementId, inProgressResultId),
    },
    payloadAction: distanceCourseDocumentRemoved,
    nextAction: {
      action: handleReloadDistanceCourseDetails,
      params: {
        payloadAction: distanceCourseDocumentRemoved,
        resultId: inProgressResultId,
      },
    },
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
  })
}

const handleCreateDistanceCourseResult =
  (
    response: ApiMiddlewarePayload<ApiCreateDistanceCourseResultResponse>,
    distanceCourseId: Guid
  ): AppThunk =>
  async dispatch => {
    const { result, error } = response
    const { resultId } = result?.createDistanceCourseResult || {}
    dispatch(createDistanceCourseResultRequested(response))
    if (!error && resultId) {
      dispatch(replace(`${routes.root}/${distanceCourseId}?resultId=${resultId}`))
      dispatch(addGuidelinesToShow(resultId))
    }
  }

export const createDistanceCourseResultRequest = (distanceCourseId: Guid) => {
  return createApiAction({
    apiCalls: {
      createDistanceCourseResult: () => createDistanceCourseResult(distanceCourseId),
    },
    payloadAction: createDistanceCourseResultRequested,
    nextAction: {
      action: handleCreateDistanceCourseResult,
      params: distanceCourseId,
    },
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
  })
}

export const submitDistanceCourseResultRequest = (resultId: Guid) => {
  return createApiAction({
    apiCalls: {
      createDistanceCourseResult: () => submitDistanceCourseResult(resultId),
    },
    payloadAction: submitDistanceCourseResultRequested,
    nextAction: {
      action: handleReloadDistanceCourseDetails,
      params: {
        payloadAction: submitDistanceCourseResultRequested,
        resultId,
      },
    },
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
  })
}
